import { Component, OnDestroy, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { KpiJourneyField } from '../../../core/models/catalog.model';
import { JOURNEY_FIELD_SCOPES } from '../../../core/config/config.constants';
import { UtilsService } from '../../../core/services/utils.service';
import { DataService } from '../../../core/services/data.service';
import { CatalogService } from '../../../core/services/catalog.service';

import {
  faSpinner,
  faInfoCircle,
  faSearch,
  faTimes,
  faGripVertical,
  faTrash,
  faBan
} from '@fortawesome/free-solid-svg-icons';

export interface JourneyScopeGroup {
  scope: string;
  items: KpiJourneyField[];
  normalItems: KpiJourneyField[];
  deletedItems: KpiJourneyField[];
  disabledItems: KpiJourneyField[];
}

@Component({
  selector: 'app-kpi-journey-selector',
  templateUrl: './kpi-journey-selector.component.html',
  styleUrls: ['./kpi-journey-selector.component.scss']
})
export class KpiJourneySelectorComponent implements OnInit, OnDestroy, OnChanges {

  @Input() selectedAudience: 'USER' | 'CUSTOMER' = 'USER';
  @Input() journeyFields: KpiJourneyField[] = [];

  private destroy$ = new Subject<void>();
  private dirtyItemKeys = new Set<string>();

  readonly SCOPES = JOURNEY_FIELD_SCOPES;

  faSpinner = faSpinner;
  faInfoCircle = faInfoCircle;
  faSearch = faSearch;
  faTimes = faTimes;
  faGripVertical = faGripVertical;
  faTrash = faTrash;
  faBan = faBan;

  isLoading = false;
  kpiList: KpiJourneyField[] = [];
  scopeGroups: JourneyScopeGroup[] = [];
  searchTerm: string = '';
  stylesheetString: string = 'aft';

  constructor(
    private utilsService: UtilsService,
    private dataService: DataService,
    private catalogService: CatalogService
  ) {}

  ngOnInit(): void {
    this.stylesheetString = this.utilsService.getStylesheetString();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['journeyFields']) {
      this.kpiList = [...(this.journeyFields ?? [])];
      this.dirtyItemKeys.clear();
      this.applyFilter();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private itemKey(kpi: KpiJourneyField): string {
    return kpi.uid ?? `${kpi.name}|${kpi.scope}`;
  }

  /**
   * Getter per la lingua corrente
   */
  get currentLang(): string {
    return this.dataService.getLanguage() || 'it-IT';
  }

  /**
   * Ottiene la label localizzata in base alla lingua corrente, con fallback su label/name
   */
  getLocalizedLabel(kpi: KpiJourneyField): string {
    if (kpi.i18nLabel && typeof kpi.i18nLabel === 'object') {
      const lang = this.currentLang;

      if (kpi.i18nLabel[lang]) {
        return kpi.i18nLabel[lang];
      }

      const matchingKey = Object.keys(kpi.i18nLabel).find(key =>
        key.toLowerCase().startsWith(lang.toLowerCase())
      );

      if (matchingKey && kpi.i18nLabel[matchingKey]) {
        return kpi.i18nLabel[matchingKey];
      }
    }
    return kpi.label || kpi.name || '';
  }

  applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    this.scopeGroups = (JOURNEY_FIELD_SCOPES as readonly string[]).map(scope => {
      const items = this.kpiList.filter(kpi => {
        if (kpi.scope !== scope) return false;
        if (!term) return true;
        return kpi.label.toLowerCase().includes(term) || kpi.name.toLowerCase().includes(term);
      });
      return {
        scope,
        items,
        normalItems: items.filter(k => !k.deletedFromCatalog && !k.disabledInCatalog),
        deletedItems: items.filter(k => k.deletedFromCatalog),
        disabledItems: items.filter(k => k.disabledInCatalog && !k.deletedFromCatalog)
      };
    }).filter(g => g.items.length > 0);
  }

  onSearchChange(): void { this.applyFilter(); }

  getScopeLabel(scope: string): string {
    return this.catalogService.getScopeLabel(scope);
  }

  /** Descrizione del gruppo (scopeDescriptions.*), mostrata nella "i" accanto al titolo */
  getScopeDescription(scope: string): string {
    return this.catalogService.getScopeDescription(scope);
  }

  get totalCount(): number {
    return this.kpiList.filter(k => !k.deletedFromCatalog && !k.disabledInCatalog).length;
  }

  get selectedCount(): number {
    return this.kpiList.filter(k => k.showInJourney && !k.deletedFromCatalog && !k.disabledInCatalog).length;
  }

  get areAllSelected(): boolean {
    const normal = this.kpiList.filter(k => !k.deletedFromCatalog && !k.disabledInCatalog);
    return normal.length > 0 && normal.every(k => k.showInJourney);
  }

  get isIndeterminate(): boolean {
    const normal = this.kpiList.filter(k => !k.deletedFromCatalog && !k.disabledInCatalog);
    const selected = normal.filter(k => k.showInJourney).length;
    return selected > 0 && selected < normal.length;
  }

  groupSelectedCount(group: JourneyScopeGroup): number {
    return group.normalItems.filter(k => k.showInJourney).length;
  }

  groupAreAllSelected(group: JourneyScopeGroup): boolean {
    return group.normalItems.length > 0 && group.normalItems.every(k => k.showInJourney);
  }

  groupIsIndeterminate(group: JourneyScopeGroup): boolean {
    const sel = group.normalItems.filter(k => k.showInJourney).length;
    return sel > 0 && sel < group.normalItems.length;
  }

  getModifiedCountForScope(scope: string): number {
    let count = 0;
    this.dirtyItemKeys.forEach(key => {
      const item = this.kpiList.find(i => this.itemKey(i) === key);
      if (item?.scope === scope) count++;
    });
    return count;
  }

  toggleSelectAll(checked: boolean): void {
    this.kpiList.filter(k => !k.deletedFromCatalog && !k.disabledInCatalog).forEach(kpi => {
      kpi.showInJourney = checked;
      this.dirtyItemKeys.add(this.itemKey(kpi));
    });
    this.applyFilter();
  }

  toggleSelectGroup(group: JourneyScopeGroup, checked: boolean): void {
    group.normalItems.forEach(kpi => {
      kpi.showInJourney = checked;
      this.dirtyItemKeys.add(this.itemKey(kpi));
    });
  }

  toggleJourneyVisibility(kpi: KpiJourneyField): void {
    kpi.showInJourney = !kpi.showInJourney;
    this.dirtyItemKeys.add(this.itemKey(kpi));
  }

  // Funzione freccia: Angular invoca il trackBy senza il contesto del componente (this non disponibile).
  // Chiave uid oppure nome+scope: lo stesso nome può esistere in più gruppi (es. "ad").
  trackByUid = (_index: number, kpi: KpiJourneyField): string => this.itemKey(kpi);

  onKpiDrop(event: CdkDragDrop<KpiJourneyField[]>, group: JourneyScopeGroup): void {
    moveItemInArray(group.normalItems, event.previousIndex, event.currentIndex);
    group.items = [...group.normalItems, ...group.deletedItems, ...group.disabledItems];
    let globalOrder = 0;
    for (const g of this.scopeGroups) {
      g.normalItems.forEach((kpi: KpiJourneyField) => {
        kpi.order = globalOrder++;
        this.dirtyItemKeys.add(this.itemKey(kpi));
      });
    }
    this.syncKpiList();
  }

  private syncKpiList(): void {
    const allItems: KpiJourneyField[] = ([] as KpiJourneyField[]).concat(...this.scopeGroups.map(g => g.items));
    const allKeys = new Set(allItems.map((a: KpiJourneyField) => this.itemKey(a)));
    this.kpiList = [
      ...allItems,
      ...this.kpiList.filter(k => !allKeys.has(this.itemKey(k)))
    ];
  }

  get hasPendingChanges(): boolean { return this.dirtyItemKeys.size > 0; }

  public resetDirtyState(): void {
    this.dirtyItemKeys.clear();
  }

  public getJourneyConfiguration(): any {
    const selectedKpis = this.kpiList
      .filter(kpi => kpi.showInJourney)
      .sort((a, b) => a.order - b.order)
      .map(kpi => ({ ...kpi }));
    return { kpiJourney: selectedKpis };
  }
}
